import React, { type PropsWithChildren, useEffect, useId, useContext, DetailedHTMLProps, HTMLAttributes } from "react";
import { createPortal } from "react-dom";
import { twMerge, twJoin } from "tailwind-merge";

import { SupBackdrop } from "../../../components/UI/Backdrop";
import SupXSmall from "../../../icons/XSmall";
import { usePortalRoot } from "../../../hooks/usePortalRoot";

import { DialogContext } from "./Dialog.context";

export interface DialogProps extends DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement> {
  open?: boolean;
  backdropClassName?: string;
  onClose?: () => void;
}

interface DialogHeaderProps extends PropsWithChildren {
  className?: string;
  onClose?: () => void;
}

interface DialogBodyProps extends PropsWithChildren {
  className?: string;
}

interface DialogFooterProps extends PropsWithChildren {
  className?: string;
}

export function SupDialogHeader({ className, children, onClose }: DialogHeaderProps) {
  return (<div className={twMerge("sup-dialog-header relative flex items-center min-h-[2.5rem] pr-4 mb-8", className)}>
      {children}
      {Boolean(onClose) && (<button className="absolute right-0 top-1/2 -translate-y-1/2 transition-colors duration-200 text-neutral-600 hover:text-neutral-700" onClick={onClose}>
          <SupXSmall />
        </button>)}
    </div>);
}

export function SupDialogBody({ className, children }: DialogBodyProps) {
  return <div className={twJoin("sup-dialog-body", className)}>{children}</div>;
}

export function SupDialogFooter({ className, children }: DialogFooterProps) {
  return <div className={twMerge("sup-dialog-footer flex mt-6", className)}>{children}</div>;
}

export function SupDialog({ children, open = false, className, backdropClassName, onClose, ...restProps }: DialogProps) {
  const modalId = useId();
  const portalRoot = usePortalRoot(open);
  const { updateDialog, removeDialog } = useContext(DialogContext);

  useEffect(() => () => {
    removeDialog(modalId);
  }, [modalId]);

  useEffect(() => {
    updateDialog(modalId, open);
  }, [modalId, open, updateDialog]);

  return <>
    {portalRoot ? createPortal(<>
      <div
        {...restProps}
        className={twMerge("sup-dialog fixed top-1/2 left-1/2 z-50 -translate-x-1/2 -translate-y-1/2 border-0.5 border-neutral-600 bg-neutral-50 shadow p-6 max-w-full", className)}
      >
        {children}
      </div>
      <SupBackdrop open className={backdropClassName} onClick={onClose} />
    </>, portalRoot) : null}
  </>;
}
